import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { asyncDelay } from '../globalUtils';
import usePageStore from './pageStore';
import { getAnimDistance } from '../../utils';
var DivState;
(function (DivState) {
    DivState["Init"] = "init";
    DivState["Start"] = "start";
    DivState["Active"] = "active";
    DivState["Out"] = "out";
    DivState["Off"] = "off";
})(DivState || (DivState = {}));
const startTransitionTime = 1;
const closeTransitionTime = 0.5;
const startTransform = 'scale(1.3)';
const activeTransform = 'translate(0, 0) scale(1)';
const closeTransform = 'scale(0.9)';
const startTransitionMethod = `${startTransitionTime}s cubic-bezier(.01,.09,.06,.99)`;
const startTransitionOpacityMethod = `${startTransitionTime * 0.8}s ease-out`;
const closeTransitionMethod = `${closeTransitionTime}s cubic-bezier(.71,0,.8,1)`;
const closeTransition = `opacity ${closeTransitionMethod}, transform  ${closeTransitionMethod}`;
const Div = ({ className, id, style, delay, subDelay, noScale, hidden, blocked, reopen, onHover, onClick, ref, children, }) => {
    const { shownPage, pageChangeCount, activePage } = usePageStore();
    const [, setRender] = useState(0);
    const containerRef = useRef(null);
    const state = useRef(DivState.Init);
    const mounted = useRef(false);
    const stateTimeout = useRef(0);
    const hover = useRef(false);
    const blockedRef = useRef(blocked);
    blockedRef.current = blocked;
    const del = (delay ?? 0) * 0.1;
    const startTransition = `opacity ${startTransitionOpacityMethod} ${del}s, transform  ${startTransitionMethod} ${del}s`;
    const innerStyle = useRef({
        opacity: 0,
        transition: startTransition,
    });
    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            handleIntersection(entries[0]?.isIntersecting);
        }, { threshold: 0.01 });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        stateTimeout.current = window.setTimeout(resetInitState, 400);
        return () => {
            clearTimeout(stateTimeout.current);
            observer.disconnect();
        };
    }, []);
    useEffect(() => {
        mounted.current = true;
    }, []);
    useEffect(() => {
        // TODO: Review blocked and replace it here with an other property
        if (shownPage !== activePage && !blocked) {
            onClose();
        }
    }, [shownPage, activePage, pageChangeCount]);
    useEffect(() => {
        if (reopen || pageChangeCount) {
            state.current = DivState.Init;
            onReveal();
        }
    }, [reopen, pageChangeCount]);
    const handleIntersection = (isIntersecting) => {
        if (isIntersecting) {
            onReveal();
        }
        else {
            onHide();
        }
    };
    const resetInitState = () => {
        if (state.current === DivState.Init) {
            state.current = DivState.Out;
            innerStyle.current = {
                opacity: 1,
                transform: activeTransform,
            };
            setRender((x) => x + 1);
        }
    };
    const onReveal = () => {
        requestAnimationFrame(onShow);
    };
    const onShow = async () => {
        const transitionSetup = blockedRef.current
            ? {}
            : {
                opacity: 1,
                transform: activeTransform,
                transition: `${startTransition}`,
            };
        if (state.current === DivState.Init) {
            if (!blockedRef.current) {
                const centerDistance = getAnimDistance(containerRef.current);
                innerStyle.current = {
                    opacity: 0,
                    transition: 'all 0s linear',
                    transform: noScale
                        ? undefined
                        : `translate(${centerDistance.x}px, ${centerDistance.y}px) ${startTransform}`,
                };
                setRender((x) => x + 1);
                await asyncDelay(0.1);
            }
            innerStyle.current = transitionSetup;
            setRender((x) => x + 1);
        }
        state.current = DivState.Active;
    };
    const onHide = async () => {
        if (state.current === DivState.Active) {
            state.current = DivState.Out;
        }
    };
    const onClose = () => {
        if (state.current === DivState.Active) {
            const centerDistance = getAnimDistance(containerRef.current, -0.4);
            innerStyle.current = {
                opacity: 0,
                transform: `translate(${centerDistance.x}px, ${centerDistance.y}px) ${closeTransform}`,
                transition: closeTransition,
            };
            setRender((x) => x + 1);
        }
        state.current = DivState.Out;
    };
    const onRef = (reference) => {
        ref?.(reference);
        containerRef.current = reference;
    };
    const onMouseLeave = () => {
        if (hover.current) {
            hover.current = false;
            onHover?.(false);
        }
    };
    const onMouseMove = () => {
        if (!hover.current) {
            hover.current = true;
            onHover?.(true);
        }
    };
    return (_jsx("div", { className: `anim-div${hidden ? ' hidden' : ''}${className ? ` ${className}` : ''}`, style: { ...innerStyle.current, ...style }, id: id, ref: onRef, onMouseLeave: onHover ? onMouseLeave : undefined, onMouseMove: onHover ? onMouseMove : undefined, onMouseEnter: onHover ? onMouseMove : undefined, onClick: onClick, children: children }));
};
export default Div;
